import { type Organization, parseOrganization } from '@/features/organization/types';
import { readNullableString, readOneOf, readRecord, readRecords, readString, readStringList } from '@/lib/readers';
import type { Membership, UserRole } from './session';

export const USER_ROLES: readonly UserRole[] = ['admin', 'member'];

/** What the session endpoints return: the user, a token, and the organizations to select from. */
export interface SessionResponse {
  token: string;
  name: string;
  email: string;
  organizationId: string | null;
  memberships: Membership[];
  organizations: Organization[];
}

/** Returns every non-empty string in the value. A permission this app does not know is kept. */
export function parsePermissions(value: unknown): string[] {
  return readStringList(value).filter(Boolean);
}

/** Returns one membership, or null when it has no organization id. */
export function parseMembership(raw: Record<string, unknown>): Membership | null {
  const organizationId = readString(raw.organization_id);
  if (!organizationId) return null;
  return {
    organizationId,
    role: readOneOf(USER_ROLES, raw.role, 'member'),
    permissions: parsePermissions(raw.permissions),
  };
}

function isMembership(membership: Membership | null): membership is Membership {
  return membership !== null;
}

/** Returns one session response, or null when it has no token. */
export function parseSessionResponse(response: unknown): SessionResponse | null {
  const raw = readRecord(response);
  const token = readString(raw.token);
  if (!token) return null;
  return {
    token,
    name: readString(raw.name),
    email: readString(raw.email),
    organizationId: readNullableString(raw.organization_id),
    memberships: readRecords(raw.memberships).map(parseMembership).filter(isMembership),
    organizations: readRecords(raw.organizations)
      .map(parseOrganization)
      .filter((organization): organization is Organization => organization !== null),
  };
}

/** Returns the active memberships in one user document, sorted by organization id. */
export function parseMemberships(raw: Record<string, unknown>): Membership[] {
  return Object.entries(readRecord(raw.organizations))
    .map(([organizationId, value]): Record<string, unknown> => ({ ...readRecord(value), organization_id: organizationId }))
    .filter((fields) => fields.is_active === true)
    .map(parseMembership)
    .filter(isMembership)
    .sort((left, right) => left.organizationId.localeCompare(right.organizationId));
}
